/**
 * Evolutionary pack derivation from ChartModel (spec §7.4, §8, §8.1a).
 * House numbers for PPP / nodes / recomputed points use Celestine Placidus cusps only.
 */

import { DEFAULT_ORBS } from "./orbs.js";

const SIGNS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

/**
 * Modern rulers (spec §8.2) — Scorpio→Pluto, Aquarius→Uranus, Pisces→Neptune.
 * @type {Readonly<Record<string, string>>}
 */
export const SIGN_RULERS = Object.freeze({
  Aries: "Mars",
  Taurus: "Venus",
  Gemini: "Mercury",
  Cancer: "Moon",
  Leo: "Sun",
  Virgo: "Mercury",
  Libra: "Venus",
  Scorpio: "Pluto",
  Sagittarius: "Jupiter",
  Capricorn: "Saturn",
  Aquarius: "Uranus",
  Pisces: "Neptune",
});

const EARTH_SIGNS = new Set(["Taurus", "Virgo", "Capricorn"]);

/** @param {number} lon */
export function normalizeLon(lon) {
  let x = Number(lon) % 360;
  if (x < 0) x += 360;
  return x;
}

/**
 * Shortest signed delta from a → b on the circle, in (-180, 180].
 * @param {number} from
 * @param {number} to
 */
export function lonDelta(from, to) {
  let d = normalizeLon(to) - normalizeLon(from);
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

/** @param {number} a @param {number} b */
export function angularSeparation(a, b) {
  return Math.abs(lonDelta(a, b));
}

/**
 * Half-open arc [start, end) on the circle (handles wrap across 0° Aries).
 * @param {number} L
 * @param {number} start
 * @param {number} end
 */
function inHouseArc(L, start, end) {
  if (start === end) return true;
  if (start < end) return L >= start && L < end;
  return L >= start || L < end;
}

/**
 * Placidus house from ChartModel.cusps (spec §8.1a).
 * Point L is in house N when L ∈ [Cusp_N, Cusp_{N+1}) along the circle.
 *
 * @param {number} longitude
 * @param {{ house: number, longitude: number }[]} cusps
 * @returns {number | null}
 */
export function houseFromCusps(longitude, cusps) {
  if (!cusps || cusps.length < 12) return null;

  const ordered = [...cusps]
    .filter((c) => c && typeof c.longitude === "number" && c.house >= 1 && c.house <= 12)
    .sort((a, b) => a.house - b.house);

  if (ordered.length < 12) return null;

  const L = normalizeLon(longitude);
  const cuspLon = ordered.map((c) => normalizeLon(c.longitude));

  for (let i = 0; i < 12; i++) {
    const start = cuspLon[i];
    const end = cuspLon[(i + 1) % 12];
    if (inHouseArc(L, start, end)) return ordered[i].house;
  }
  return null;
}

/** @param {number} longitude */
export function signNameFromLon(longitude) {
  const lon = normalizeLon(longitude);
  return SIGNS[Math.floor(lon / 30) % 12];
}

/** @param {number} longitude */
export function degreeMinuteFromLon(longitude) {
  const lon = normalizeLon(longitude);
  const inSign = lon % 30;
  const degree = Math.floor(inSign);
  const minute = Math.floor((inSign - degree) * 60);
  return { degree, minute };
}

/** @param {number} longitude */
export function formatLon(longitude) {
  const sign = signNameFromLon(longitude);
  const { degree, minute } = degreeMinuteFromLon(longitude);
  return `${String(degree).padStart(2, "0")}°${String(minute).padStart(2, "0")}' ${sign}`;
}

/**
 * Normalize Celestine / display body names (spec §8.1).
 * @param {string} name
 */
export function normalizeBodyName(name) {
  const s = String(name || "").trim();
  if (/^(true|mean)\s+north\s+node$/i.test(s) || /^north\s+node$/i.test(s)) {
    return "North Node";
  }
  if (/^(true|mean)\s+south\s+node$/i.test(s) || /^south\s+node$/i.test(s)) {
    return "South Node";
  }
  if (/^asc(endant)?$/i.test(s)) return "Ascendant";
  if (/^desc(endant)?$/i.test(s) || /^dsc$/i.test(s)) return "Descendant";
  if (/^midheaven$/i.test(s) || /^mc$/i.test(s)) return "Midheaven";
  if (/^imum\s*coeli$/i.test(s) || /^ic$/i.test(s)) return "Imum Coeli";
  return s;
}
/**
 * @param {object} row - body or angle-like row from ChartModel
 * @param {{ house: number, longitude: number }[]} cusps
 * @param {{ forceCuspHouse?: boolean }} [opts]
 */
export function toLonPoint(row, cusps, opts = {}) {
  if (!row) return null;
  const name = normalizeBodyName(row.name);
  const longitude = normalizeLon(row.longitude);
  let house =
    row.house != null && Number.isFinite(Number(row.house))
      ? Number(row.house)
      : null;

  if (opts.forceCuspHouse || house == null) {
    house = houseFromCusps(longitude, cusps);
  }

  const dm =
    row.degree != null && row.minute != null
      ? { degree: Number(row.degree), minute: Number(row.minute) }
      : degreeMinuteFromLon(longitude);

  return {
    name,
    longitude,
    signName: row.signName || signNameFromLon(longitude),
    degree: dm.degree,
    minute: dm.minute,
    formatted: row.formatted || formatLon(longitude),
    house,
    isRetrograde: Boolean(row.isRetrograde),
    kind: row.kind || "planet",
  };
}

/**
 * @param {import("../celestine-adapter.js").ChartModel} model
 * @param {Partial<typeof DEFAULT_ORBS>} [orbOverrides]
 */
export function deriveEvolutionaryPack(model, orbOverrides = {}) {
  const orbs = { ...DEFAULT_ORBS, ...orbOverrides };
  const cusps = model.cusps || [];

  const bodyPoints = (model.bodies || []).map((b) => toLonPoint(b, cusps));
  const anglePoints = (model.angles || []).map((a) =>
    toLonPoint({ ...a, kind: "angle" }, cusps)
  );

  const byName = new Map();
  for (const p of [...bodyPoints, ...anglePoints]) {
    if (p) byName.set(p.name, p);
  }

  const plutoRaw = byName.get("Pluto");
  if (!plutoRaw) {
    throw new Error("deriveEvolutionaryPack: Pluto missing from ChartModel");
  }
  // Always assign Pluto house from Placidus cusps so payload matches table logic under interception
  const pluto = {
    ...plutoRaw,
    house: houseFromCusps(plutoRaw.longitude, cusps) ?? plutoRaw.house,
  };
  byName.set("Pluto", pluto);


  const planetsAndPoints = bodyPoints.filter(
    (b) =>
      b &&
      b.kind === "planet" &&
      b.name !== "North Node" &&
      b.name !== "South Node"
  );

  const { north, south } = resolveNodes(model, byName, cusps);

  const pppLon = normalizeLon(pluto.longitude + 180);
  const pppDm = degreeMinuteFromLon(pppLon);
  const ppp = {
    longitude: pppLon,
    signName: signNameFromLon(pppLon),
    degree: pppDm.degree,
    minute: pppDm.minute,
    house: houseFromCusps(pppLon, cusps),
    formatted: formatLon(pppLon),
  };

  let chiron = byName.get("Chiron");
  if (chiron) {
    chiron = {
      ...chiron,
      house:
        chiron.house != null
          ? chiron.house
          : houseFromCusps(chiron.longitude, cusps),
    };
  }

  const hitCandidates = [
    ...planetsAndPoints.filter((p) => p && p.name !== "Pluto"),
    ...anglePoints,
  ].filter(Boolean);

  const plutoConjunct = hitsWithin(
    hitCandidates.filter((p) => p.name !== "Pluto"),
    pluto.longitude,
    orbs.plutoConjunctOrb
  );

  const onNorthNode = north
    ? hitsWithin(
        hitCandidates.filter(
          (p) => p.name !== "North Node" && p.name !== "South Node"
        ),
        north.longitude,
        orbs.nodeConjunctOrb
      )
    : [];

  const onSouthNode = south
    ? hitsWithin(
        hitCandidates.filter(
          (p) => p.name !== "North Node" && p.name !== "South Node"
        ),
        south.longitude,
        orbs.nodeConjunctOrb
      )
    : [];

  const squaresToNodes = collectNodalSquares(
    hitCandidates,
    north,
    south,
    orbs.nodeSquareOrb
  );

  const asc = byName.get("Ascendant");
  const dsc = byName.get("Descendant");
  const mc = byName.get("Midheaven");
  const ic = byName.get("Imum Coeli");

  const angularHits = {
    asc: asc
      ? hitsWithin(planetsAndPoints, asc.longitude, orbs.angleConjunctOrb)
      : [],
    dsc: dsc
      ? hitsWithin(planetsAndPoints, dsc.longitude, orbs.angleConjunctOrb)
      : [],
    mc: mc
      ? hitsWithin(planetsAndPoints, mc.longitude, orbs.angleConjunctOrb)
      : [],
    ic: ic
      ? hitsWithin(planetsAndPoints, ic.longitude, orbs.angleConjunctOrb)
      : [],
  };

  const snSign = south?.signName || null;
  const nnSign = north?.signName || null;
  const southRulerName = snSign ? SIGN_RULERS[snSign] || null : null;
  const northRulerName = nnSign ? SIGN_RULERS[nnSign] || null : null;

  const nodalRulers = {
    southRuler: {
      planet: southRulerName || "",
      body: southRulerName ? byName.get(southRulerName) || null : null,
    },
    northRuler: {
      planet: northRulerName || "",
      body: northRulerName ? byName.get(northRulerName) || null : null,
    },
  };

  const proxyHint = buildProxyHint({
    onNorthNode,
    north,
    asc,
    northRulerName,
    northRulerBody: nodalRulers.northRuler.body,
    orbs,
  });

  const stelliums = findStelliums(planetsAndPoints, orbs);

  return {
    pluto: { body: pluto, conjunct: plutoConjunct, ppp },
    nodalRulers,
    catalyst: { onNorthNode, proxyHint },
    pastMastery: { onSouthNode },
    skippedSteps: { squaresToNodes },
    angularHits,
    stelliums,
    saturn: byName.get("Saturn") || null,
    chiron: chiron || null,
    uranus: byName.get("Uranus") || null,
    neptune: byName.get("Neptune") || null,
    moon: byName.get("Moon") || null,
    sovereigntyFlags: {
      ancestralWeight: computeAncestralWeight({
        pluto,
        saturn: byName.get("Saturn"),
        ic,
        angularHits,
      }),
      enmeshmentRisk: computeEnmeshmentRisk({ south, pluto, onSouthNode }),
      groundedPath: computeGroundedPath({ north }),
    },
    _nodes: { north, south },
  };
}

/**
 * @param {import("../celestine-adapter.js").ChartModel} model
 * @param {Map<string, object>} byName
 * @param {object[]} cusps
 */
function resolveNodes(model, byName, cusps) {
  let north = byName.get("North Node");
  if (!north) {
    const rawNn = (model.bodies || []).find((b) => /north\s*node/i.test(b.name));
    if (rawNn) north = toLonPoint(rawNn, cusps, { forceCuspHouse: true });
  }
  if (north) {
    north = {
      ...north,
      name: "North Node",
      kind: "node",
      house: north.house ?? houseFromCusps(north.longitude, cusps),
    };
  }

  let south = byName.get("South Node");
  if (!south && north) {
    const snLon = normalizeLon(north.longitude + 180);
    const dm = degreeMinuteFromLon(snLon);
    south = {
      name: "South Node",
      longitude: snLon,
      signName: signNameFromLon(snLon),
      degree: dm.degree,
      minute: dm.minute,
      formatted: formatLon(snLon),
      house: houseFromCusps(snLon, cusps),
      isRetrograde: false,
      kind: "node",
    };
  } else if (south) {
    south = {
      ...south,
      name: "South Node",
      kind: "node",
      house: south.house ?? houseFromCusps(south.longitude, cusps),
    };
  }

  return { north: north || null, south: south || null };
}

/**
 * @param {object[]} candidates
 * @param {number} targetLon
 * @param {number} orbMax
 */
function hitsWithin(candidates, targetLon, orbMax) {
  const hits = [];
  for (const p of candidates) {
    if (!p) continue;
    const orb = angularSeparation(p.longitude, targetLon);
    if (orb <= orbMax) hits.push(toHit(p, orb));
  }
  hits.sort((a, b) => a.orb - b.orb);
  return hits;
}

/** @param {object} p @param {number} orb */
function toHit(p, orb) {
  return {
    name: p.name,
    kind: p.kind || "planet",
    orb,
    longitude: p.longitude,
    signName: p.signName,
    house: p.house ?? null,
  };
}

function collectNodalSquares(hitCandidates, north, south, nodeSquareOrb) {
  const squaresToNodes = [];
  if (!north || !south) return squaresToNodes;
  const seen = new Set();
  for (const p of hitCandidates) {
    if (p.name === "North Node" || p.name === "South Node") continue;
    const dNn = angularSeparation(p.longitude, north.longitude);
    const dSn = angularSeparation(p.longitude, south.longitude);
    const best = Math.min(Math.abs(dNn - 90), Math.abs(dSn - 90));
    if (best <= nodeSquareOrb && !seen.has(p.name)) {
      seen.add(p.name);
      squaresToNodes.push(toHit(p, best));
    }
  }
  squaresToNodes.sort((a, b) => a.orb - b.orb);
  return squaresToNodes;
}

function buildProxyHint({
  onNorthNode,
  north,
  asc,
  northRulerName,
  northRulerBody,
  orbs,
}) {
  if (onNorthNode && onNorthNode.length > 0) return null;
  if (!north) return null;

  const parts = [];
  if (asc) {
    const d = angularSeparation(asc.longitude, north.longitude);
    if (d <= orbs.nodeConjunctOrb) {
      parts.push(
        `Ascendant within ${d.toFixed(2)}° of North Node (orb ${orbs.nodeConjunctOrb}°)`
      );
    }
  }
  if (northRulerName) {
    if (northRulerBody) {
      parts.push(
        `North Node ruler ${northRulerName} in ${northRulerBody.signName}` +
          (northRulerBody.house != null ? ` house ${northRulerBody.house}` : "")
      );
    } else {
      parts.push(`North Node ruler ${northRulerName}`);
    }
  }
  if (!parts.length) {
    return "No body within node conjunct orb of North Node; use nodal ruler emphasis in prose";
  }
  return parts.join("; ");
}

/**
 * @param {object[]} planets
 * @param {typeof DEFAULT_ORBS} orbs
 */
function findStelliums(planets, orbs) {
  /** @type {Array<{ mode: string, key: string, members: string[], spanDegrees?: number }>} */
  const out = [];
  const list = planets.filter(Boolean);

  /** @type {Map<string, object[]>} */
  const bySign = new Map();
  /** @type {Map<string, object[]>} */
  const byHouse = new Map();

  for (const p of list) {
    if (!bySign.has(p.signName)) bySign.set(p.signName, []);
    bySign.get(p.signName).push(p);
    if (p.house != null) {
      const hk = String(p.house);
      if (!byHouse.has(hk)) byHouse.set(hk, []);
      byHouse.get(hk).push(p);
    }
  }

  for (const [sign, members] of bySign) {
    if (members.length < orbs.stelliumMinBodies) continue;
    const lons = members
      .map((m) => normalizeLon(m.longitude))
      .sort((a, b) => a - b);
    const span = clusterSpan(lons);
    if (span > orbs.stelliumMaxSpan) continue;
    out.push({
      mode: "sign",
      key: sign,
      members: members.map((m) => m.name),
      spanDegrees: span,
    });
  }

  for (const [house, members] of byHouse) {
    if (members.length < orbs.stelliumMinBodies) continue;
    out.push({
      mode: "house",
      key: house,
      members: members.map((m) => m.name),
    });
  }

  return out;
}

/** Smallest arc covering all points on the circle (degrees). */
function clusterSpan(sortedLons) {
  const n = sortedLons.length;
  if (n < 2) return 0;
  let minSpan = 360;
  for (let i = 0; i < n; i++) {
    const prev = sortedLons[(i + n - 1) % n];
    const span = (prev - sortedLons[i] + 360) % 360;
    if (span < minSpan) minSpan = span;
  }
  const linear = sortedLons[n - 1] - sortedLons[0];
  if (linear < minSpan) minSpan = linear;
  return minSpan;
}

function computeAncestralWeight({ pluto, saturn, ic, angularHits }) {
  if (pluto?.house === 4) return true;
  if (saturn?.house === 4) return true;
  if (ic && pluto && angularSeparation(pluto.longitude, ic.longitude) <= 8) {
    return true;
  }
  if (ic && saturn && angularSeparation(saturn.longitude, ic.longitude) <= 8) {
    return true;
  }
  if (angularHits?.ic?.length >= 2) return true;
  return false;
}

function computeEnmeshmentRisk({ south, pluto, onSouthNode }) {
  if (south && (south.house === 7 || south.house === 8)) return true;
  if (pluto && (pluto.house === 7 || pluto.house === 8)) return true;
  if (
    onSouthNode?.some(
      (h) =>
        h.house === 7 ||
        h.house === 8 ||
        h.name === "Jupiter" ||
        h.name === "Venus"
    )
  ) {
    return true;
  }
  return false;
}

function computeGroundedPath({ north }) {
  if (!north) return false;
  if (EARTH_SIGNS.has(north.signName)) return true;
  if (north.house === 2 || north.house === 6 || north.house === 10) return true;
  return false;
}

